import React from "react";
import { CircleAlert, CircleCheckBig, Info } from "lucide-react";
import SidebarContents from "../../components/SidebarContents";
import SectionContents from "../../components/SectionContents";

const sections = [
{
    sectionId: "purpose",
    sectionTitle: "1. Purpose",
    sectionBody: (
      <>
        <div className="space-y-4 text-sm leading-relaxed">
          <p>
            Everyone at Hyvid has the right to raise a concern about their employment or working conditions and to have that concern heard, investigated, and resolved fairly. This policy sets out how you do that — and what Hyvid commits to in return.
          </p>

          <p>
            A grievance can be any work-related concern: a disagreement with a decision affecting your employment, a concern about how you have been treated, a perceived breach of your rights, or any other issue that affects your work that you cannot resolve informally on your own.
          </p>

          <div className="flex gap-3 p-3 rounded-lg border bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700">
            <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              This single process applies to all workplace complaints, including complaints of harassment and gender-based sexual harassment. For harassment complaints, the Committee on Decorum and Investigation (CODI) steps in as the investigating body at Step 3 — everything else follows the same process.
            </div>
          </div>
        </div>
      </>
    ),
  },

  {
    sectionId: "principles",
    sectionTitle: "2. Our Principles",
    sectionBody: (
      <div className="space-y-4 text-sm leading-relaxed">
        <div className="grid sm:grid-cols-2 gap-3">

          {[
            {
              title: "Fairness",
              desc: "Every grievance will be taken seriously and assessed on its merits.",
              color: "from-emerald-400 to-green-400",
            },
            {
              title: "Timeliness",
              desc: "We will respond and resolve within the timeframes set out in this policy.",
              color: "from-blue-400 to-cyan-400",
            },
            {
              title: "Confidentiality",
              desc: "Information about your grievance will only be shared with those who need to know.",
              color: "from-purple-400 to-violet-400",
            },
            {
              title: "No retaliation",
              desc: "Raising a grievance in good faith will never be used against you.",
              color: "from-amber-400 to-orange-400",
            },
            {
              title: "Due process",
              desc: "All parties will have the opportunity to be heard before any decision is made.",
              color: "from-teal-400 to-emerald-400",
            },
            {
              title: "Impartiality",
              desc: "Anyone with a conflict of interest in a complaint must declare it and step aside.",
              color: "from-pink-400 to-rose-400",
            },
          ].map((item) => (
            <div
              key={item.title}
              className="flex items-start gap-3 p-3 rounded-lg dark-mode-card border border-gray-100 shadow-sm"
            >
              <div className={`w-2 min-h-6 rounded-full bg-gradient-to-b ${item.color} flex-shrink-0 mt-0.5`} />
              <div>
                <span className="font-semibold title-text text-sm">{item.title}</span>
                <p className="text-xs muted-text mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}

        </div>
      </div>
    ),
  },
  {
  sectionId: "coverage",
  sectionTitle: "3. What This Policy Covers",
  sectionBody: (
    <>
      <div className="space-y-4 text-sm  leading-relaxed">
        <p>You can use this process to raise concerns about:</p>

        <ul className="list-none space-y-1.5 pl-2">
          {[
            "How you have been treated by a manager, colleague, or client.",
            "A decision about your pay, benefits, working conditions, or employment status.",
            "Perceived discrimination, bullying, or unfair treatment.",
            "Harassment or gender-based sexual harassment.",
            "Breach of a Hyvid policy or your employment agreement.",
            "Any other work-related matter you believe has not been dealt with appropriately.",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </>
  ),
},
{
  sectionId: "process",
  sectionTitle: "4. The Grievance Process",
  sectionBody: (
    <>
      <div className="space-y-4 text-sm  leading-relaxed">

        <div className="pt-2 space-y-6">

          {[
            {
              step: "1",
              title: "Step 1 — Informal Resolution (preferred first step)",
              content: (
                <>
                  <p>
                    Most workplace issues can be resolved through an honest conversation. If you have a concern, try raising it directly with your manager first. This is the fastest path to resolution and is encouraged where you feel comfortable doing so.
                  </p>

                  <p>
                    If your concern involves your manager, or you do not feel comfortable raising it directly, go straight to Step 2.
                  </p>

                  <div className="flex gap-3 p-3 rounded-lg border bg-amber-50 border-amber-200 dark:bg-amber-900/20  dark:border-amber-700">
                    <CircleAlert className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-semibold mb-1 text-amber-800 dark:text-amber-200">
                        Note for harassment complaints
                      </p>
                      Informal resolution is only appropriate for harassment complaints if the complainant genuinely prefers it and both parties consent. It is never a substitute for a formal process where the conduct is serious. The CODI will assess this at Step 3.
                    </div>
                  </div>
                </>
              ),
            },

            {
              step: "2",
              title: "Step 2 — Formal Written Complaint",
              content: (
                <>
                  <p>
                    If informal discussion does not resolve your concern, or is not appropriate, submit a formal written complaint via HyvidOne. Your complaint should include:
                  </p>

                  <ul className="list-none space-y-1.5 pl-2">
                    {[
                      "A clear description of the issue and relevant dates or events.",
                      "The names of anyone involved.",
                      "What outcome you are seeking.",
                      "Any supporting evidence or documentation you wish to provide.",
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  <p>General grievances are submitted to <strong>People &amp; Culture</strong>.</p>
                  <p>
                    Harassment complaints are submitted to the <strong>CODI Secretariat</strong> or People &amp; Culture, who will immediately forward them to the CODI. CODI contact details are available in HyvidOne.
                  </p>

                  <div className="flex gap-3 p-3 rounded-lg border bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-700">
                    <CircleCheckBig className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      You will receive a written acknowledgement within <strong>two (2) working days</strong> of your complaint being received.
                    </div>
                  </div>
                </>
              ),
            },

            {
              step: "3",
              title: "Step 3 — Investigation and Decision",
              content: (
                <>
                  <p>
                    People &amp; Culture will review the complaint and determine whether a formal investigation is needed. Where an investigation proceeds, both the complainant and any respondent will have the opportunity to present their account, witnesses and evidence will be considered, and a written decision will be issued within the timeframes below.
                  </p>

                  <p>
                    The outcome may include corrective action, a policy change, a change to working arrangements, or a finding that the complaint could not be substantiated — along with clear reasons.
                  </p>

                  <div className="flex gap-3 p-3 rounded-lg border bg-amber-50 border-amber-200 dark:bg-amber-900/20  dark:border-amber-700">
                    <CircleAlert className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-semibold mb-1 text-amber-800 dark:text-amber-200">
                        Harassment complaints — CODI takes the lead
                      </p>

                      <p>
                        Where a complaint involves harassment or gender-based sexual harassment, the CODI conducts the investigation in place of People &amp; Culture. The CODI is Hyvid's Committee on Decorum and Investigation, constituted as required by the Safe Spaces Act (RA 11313). It is led by a woman and has majority women members.
                      </p>

                      <p className="mt-2">
                        Two rules apply specifically to harassment complaints that do not apply to general grievances:
                      </p>

                      <ol className="list-decimal pl-5 space-y-1 mt-1">
                        <li>The CODI must investigate regardless of whether the complainant wishes to proceed formally — this is a legal obligation under RA 11313.</li>
                        <li>Breaching confidentiality in a harassment investigation carries specific penalties under RA 11313, in addition to Hyvid's own disciplinary consequences.</li>
                      </ol>

                      <p className="mt-2">
                        In all other respects — timeframes, appeal rights, retaliation protection, record-keeping — the process is identical.
                      </p>
                    </div>
                  </div>
                </>
              ),
            },

            {
              step: "4",
              title: "Step 4 — Appeal",
              content: (
                <>
                  <p>
                    If you are not satisfied with the outcome, you may appeal the decision in writing to a Director or the CEO within <strong>five (5) working days</strong> of receiving the written decision. Appeals should state clearly why you believe the outcome was incorrect or unfair. The appeal decision is final within Hyvid's internal process.
                  </p>

                  <div className="flex gap-3 p-3 rounded-lg border bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700">
                    <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      Nothing in this policy prevents you from filing a complaint with DOLE or the NLRC at any stage if you prefer to pursue an external remedy.
                    </div>
                  </div>
                </>
              ),
            },
          ].map((s) => (
            <div key={s.step} className="relative pl-10 pb-6">
              <div className="absolute left-0 top-0 w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm shadow-sm">
                {s.step}
              </div>

              <div className="dark-mode-card border border-gray-200 rounded-xl p-4 shadow-sm ml-2">
                <h4 className="font-bold title-text text-sm mb-2">
                  {s.title}
                </h4>
                <div className="text-sm  leading-relaxed space-y-2">
                  {s.content}
                </div>
              </div>
            </div>
          ))}

        </div>
      </div>
    </>
  ),
},
{
  sectionId: "interim",
  sectionTitle: "5. Interim Protective Measures",
  sectionBody: (
    <div className="space-y-4 text-sm  leading-relaxed">
      <p>
        Where a complaint poses a risk to a party's safety or wellbeing while the investigation proceeds, People &amp; Culture (or the CODI for harassment complaints) may implement interim measures, such as:
      </p>

      <ul className="list-none space-y-1.5 pl-2">
        <li className="flex items-start gap-2">
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
          <span>
            Temporary separation of the complainant and respondent from shared meetings, channels, or working groups.
          </span>
        </li>

        <li className="flex items-start gap-2">
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
          <span>
            Temporary adjustment of reporting lines or work assignments.
          </span>
        </li>

        <li className="flex items-start gap-2">
          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
          <span>
            Preventive suspension of the respondent (without pay for up to 30 days, with pay beyond that) where their continued presence poses a serious and imminent risk.
          </span>
        </li>
      </ul>

      <div className="flex gap-3 p-3 rounded-lg border bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700">
        <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          Interim measures are not a finding of guilt. They are temporary and will be reviewed once the investigation is concluded.
        </div>
      </div>
    </div>
  ),
},

{
  sectionId: "retaliation",
  sectionTitle: "6. Protection from Retaliation",
  sectionBody: (
    <div className="space-y-4 text-sm  leading-relaxed">
      <div className="flex gap-3 p-3 rounded-lg border bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-700">
        <CircleCheckBig className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          Hyvid will not tolerate any form of retaliation against an employee who raises a grievance in good faith, participates in an investigation, or supports a colleague doing so. Retaliation is itself a serious breach of this policy and will be subject to disciplinary action.
        </div>
      </div>

      <p>
        If you believe you have experienced retaliation for raising a grievance, report it to <strong>People &amp; Culture</strong> immediately.
      </p>
    </div>
  ),
},

{
  sectionId: "confidentiality",
  sectionTitle: "7. Confidentiality",
  sectionBody: (
    <div className="space-y-4 text-sm  leading-relaxed">
      <p>
        All information about a grievance — including that a complaint has been made — is confidential. This applies to the complainant, the respondent, any witnesses, and all Hyvid personnel involved in managing the complaint. Breaching confidentiality during or after a grievance process is a disciplinary matter.
      </p>

      <div className="flex gap-3 p-3 rounded-lg border bg-amber-50 border-amber-200 dark:bg-amber-900/20  dark:border-amber-700">
        <CircleAlert className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          For harassment complaints, confidentiality obligations also carry legal consequences under <strong>RA 11313</strong>.
        </div>
      </div>
    </div>
  ),
},
{
  sectionId: "records",
  sectionTitle: "8. Records",
  sectionBody: (
    <div className="space-y-4 text-sm  leading-relaxed">
      <p>
        Hyvid will keep a written record of all formal grievances and their outcomes. Records are maintained confidentially in accordance with the <strong>Data Privacy Act (RA 10173)</strong> and retained for a minimum of <strong>five (5) years</strong>. Access is restricted to those with a legitimate need to know.
      </p>
    </div>
  ),
},

{
  sectionId: "external",
  sectionTitle: "9. External Avenues",
  sectionBody: (
    <div className="space-y-4 text-sm  leading-relaxed">
      <p>At any time, you have the right to raise a concern directly with:</p>

      <div className="space-y-2 mt-1">
        {[
          {
            label: "DOLE",
            desc: "For labour standards enforcement. DOLE also conducts annual inspections of employer compliance with RA 11313 for harassment complaints.",
          },
          {
            label: "NLRC",
            desc: "For illegal dismissal, constructive dismissal, or other labour disputes.",
          },
          {
            label: "National Conciliation and Mediation Board (NCMB)",
            desc: "For voluntary conciliation.",
          },
        ].map((item) => (
          <div
            key={item.label}
            className="flex items-start gap-3 p-3 rounded-lg dark-mode-card border border-gray-100"
          >
            <span className="flex-shrink-0 px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-800 dark:text-purple-200 text-xs font-bold whitespace-nowrap">
              {item.label}
            </span>
            <p className="text-sm muted-text">{item.desc}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-3 p-3 rounded-lg border bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-700">
        <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          Using Hyvid's internal grievance process does not waive your right to use these external channels.
        </div>
      </div>
    </div>
  ),
},

{
  sectionId: "questions",
  sectionTitle: "10. Questions",
  sectionBody: (
    <div>
      <div className="space-y-4 text-sm leading-relaxed">
        <p>
          If you have any questions about this process or are unsure how to raise a concern, contact <strong>People &amp; Culture</strong> via HyvidOne. For harassment complaints, you can also contact the CODI directly — details are in HyvidOne. Everything discussed will be treated confidentially.
        </p>
      </div>
      <div className="mt-8 p-4 bg-gray-100 dark:bg-gray-800/20 rounded-xl border border-gray-200 dark:border-gray-700">
        <p className="text-xs muted-text italic">
          <strong>Note:</strong> This policy should be reviewed by Philippine legal counsel before use. Legal references: Labor Code of the Philippines, RA 11313 (Safe Spaces Act), RA 7877 (Anti-Sexual Harassment Act), DOLE DO 237-22, RA 10173 (Data Privacy Act).
        </p>
      </div>
    </div>
  ),
},

];

const GrievanceDisputeContents = () => {
  return (
    <div  className="flex items-start">
      <SidebarContents
        sections={sections} 
      />
      <div  className="flex-1 p-6 space-y-6 max-w-4xl">
        {sections.map((section) => (
          <SectionContents
            key={section.sectionId}
            sectionId={section.sectionId}
            sectionTitle={section.sectionTitle}
            sectionBody={section.sectionBody}
          />
        ))}
      </div>
    </div>
  );
};

export default GrievanceDisputeContents;